/**
 * Bounded-concurrency runner for attachment uploads (#522).
 *
 * Uploads ran strictly one file at a time everywhere, so N photos cost N times
 * a single upload. This is the one place the concurrency limit lives; call
 * sites never choose it.
 *
 * Two properties the callers depend on:
 *
 *  - it SETTLES rather than rejecting, so one unreadable file cannot abandon
 *    the rest of a batch;
 *  - results are INDEX-ALIGNED with the input, because callers map positions
 *    back to intent (which photo the user picked as the cover, which folder a
 *    file belongs in). A compacted "successes only" array silently shifts
 *    those mappings when an earlier file fails.
 */

/**
 * Three in flight, chosen for the dev lane rather than for bandwidth.
 *
 * Vite's dev server is plain HTTP/1.1, so the browser allows 6 sockets per
 * origin and the app's EventSource permanently holds one. At 3 there are still
 * two free for ordinary GETs and, more importantly, for the SSE stream to
 * reconnect — saturating the pool makes the "Reconnecting live updates" banner
 * (#574) measurably worse. Production is HTTP/2 behind Caddy and multiplexed,
 * so this bound only costs throughput in dev.
 *
 * Parallelism does not create uplink; what it hides is per-request setup plus
 * the server's streaming hash and thumbnail work. Three covers that overlap.
 * The API caps a single file at 15MB, so this bounds concurrent transfer at
 * roughly 45MB.
 */
export const UPLOAD_CONCURRENCY = 3;

export type UploadOutcome<T> =
  | { status: "fulfilled"; file: File; value: T }
  | { status: "rejected"; file: File; reason: unknown };

export type RunWithConcurrencyOptions<T> = {
  /** Defaults to UPLOAD_CONCURRENCY; present so tests can pin it. */
  limit?: number;
  /** Fires as each file settles, in completion order, with its input index. */
  onSettled?: (outcome: UploadOutcome<T>, index: number) => void;
};

/**
 * Run `upload` over `files`, at most `limit` at a time. Never rejects: the
 * returned array is index-aligned with `files` and every entry is settled.
 */
export async function runWithConcurrency<T>(
  files: File[],
  upload: (file: File, index: number) => Promise<T>,
  options: RunWithConcurrencyOptions<T> = {}
): Promise<Array<UploadOutcome<T>>> {
  const limit = Math.max(1, Math.floor(options.limit ?? UPLOAD_CONCURRENCY));
  const results: Array<UploadOutcome<T>> = new Array(files.length);
  let cursor = 0;

  // Shared cursor rather than fixed slices, so a slow file does not idle a
  // worker that could be starting the next one.
  async function worker(): Promise<void> {
    for (;;) {
      const index = cursor;
      cursor += 1;
      const file = files[index];
      if (!file) return;

      let outcome: UploadOutcome<T>;
      try {
        outcome = { status: "fulfilled", file, value: await upload(file, index) };
      } catch (reason) {
        outcome = { status: "rejected", file, reason };
      }
      results[index] = outcome;
      options.onSettled?.(outcome, index);
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, files.length) }, () => worker()));
  return results;
}
